import React from "react";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CHART } from "@/pages/admin/compliance/analytics/components/primitives";
import { fmt, fmtCompact, fmtPct, pctOf } from "./format";

/**
 * A ranked horizontal bar chart — the one chart shape these pages need.
 *
 * Horizontal, because every label here is a name: a tenant, a tool. Vertical
 * bars forced the labels to a 25° angle and truncated them, which is what made
 * the old "Tokens by tenant" chart a decoration rather than something a person
 * could read a name off.
 *
 * Ranked and truncated to `limit`, because the question this chart answers is
 * "who is heavy", not "what is everyone" — the table under it is the full list.
 * The truncation is stated in the caption, never left for the reader to notice.
 *
 * One colour, on purpose. The bars are one measure of one thing, so length
 * carries the whole comparison; colouring each bar differently would imply a
 * category that does not exist. `CHART.indigo` is the app's primary data mark.
 *
 * Every colour is explicit. Recharts' defaults — a near-black bar, grey ticks,
 * a white tooltip — follow no theme, and on the dark background the result was
 * close to invisible.
 */
const ChartTooltip = ({ active, payload, total, valueLabel }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-border-primary bg-background-secondary px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-text-primary">{row.name}</p>
      {row.sublabel ? <p className="text-[11px] text-text-secondary">{row.sublabel}</p> : null}
      <p className="mt-1 text-[11px] text-text-secondary">
        {valueLabel}: <span className="font-semibold text-text-primary tabular-nums">{fmt(row.value)}</span>
        {total ? <span className="ml-1 text-text-secondary">({fmtPct(pctOf(row.value, total))} of total)</span> : null}
      </p>
    </div>
  );
};

const ROW_HEIGHT = 30;

const UsageRankedBars = ({ data = [], valueLabel = "Total tokens", total, onSelect, emptyNote = "Nothing to chart in this period." }) => {
  if (data.length === 0) {
    return <p className="rounded-lg border border-dashed border-border-primary p-8 text-center text-sm text-text-secondary">{emptyNote}</p>;
  }

  const clickable = typeof onSelect === "function";
  const longestLabel = data.reduce((max, row) => Math.max(max, String(row.name ?? "").length), 0);
  // Enough room for the names actually present, capped so one long name cannot
  // squeeze the bars into nothing.
  const axisWidth = Math.min(180, Math.max(88, longestLabel * 7));

  return (
    <div style={{ height: data.length * ROW_HEIGHT + 16 }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 52, bottom: 4, left: 0 }} barCategoryGap="22%">
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={axisWidth}
            tick={{ fontSize: 11, fill: "currentColor" }}
            className="text-text-secondary"
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => (String(value).length > 22 ? `${String(value).slice(0, 21)}…` : value)}
          />
          <Tooltip content={<ChartTooltip total={total} valueLabel={valueLabel} />} cursor={{ fill: CHART.slate, fillOpacity: 0.1 }} />
          <Bar
            dataKey="value"
            fill={CHART.indigo}
            radius={[0, 4, 4, 0]}
            maxBarSize={18}
            isAnimationActive={false}
            cursor={clickable ? "pointer" : "default"}
            // Recharts hands the bar's own datum to the handler, but the shape
            // differs between versions — the id is read from either place.
            onClick={clickable ? (entry) => onSelect(entry?.id ?? entry?.payload?.id) : undefined}
          >
            {/* `currentColor` plus a token class, so the value labels follow
                the theme the same way the axis ticks do. A literal fill would
                vanish on one theme or the other. */}
            <LabelList dataKey="value" position="right" formatter={fmtCompact} className="text-text-secondary" style={{ fontSize: 11, fill: "currentColor" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default UsageRankedBars;
